import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import TripBookingForm from './TripBookingForm';

const RiderDashboard = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('book');
  const [tripHistoryTab, setTripHistoryTab] = useState('current');
  const [trips, setTrips] = useState([]);
  const [completedTrips, setCompletedTrips] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [ratingTrip, setRatingTrip] = useState(null);
  const [rating, setRating] = useState(0);
  const [feedback, setFeedback] = useState('');
  const [submittingRating, setSubmittingRating] = useState(false);

  const [cancellingTrip, setCancellingTrip] = useState(null);
  const [reBookData, setReBookData] = useState(null);
  const [driverNotification, setDriverNotification] = useState(null);
  const [payingTrip, setPayingTrip] = useState(null);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

  // Auto-load trips when switching to trips tab
  useEffect(() => {
    if (activeTab === 'trips') {
      loadTrips();
    }
  }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  // Listen for header events to switch tabs
  useEffect(() => {
    const handleHeaderNavigation = (event) => {
      if (event.detail === 'trip-history') {
        setActiveTab('trips');
      }
    };

    window.addEventListener('headerNavigation', handleHeaderNavigation);
    return () => window.removeEventListener('headerNavigation', handleHeaderNavigation);
  }, []);

  // Auto-refresh trips every 30 seconds when on trips tab
  useEffect(() => {
    let interval;
    if (activeTab === 'trips') {
      interval = setInterval(() => {
        loadTrips();
      }, 30000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadTrips = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Load all user's bookings
      const response = await fetch(`${BACKEND_URL}/api/bookings/user/${user.id}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      });

      if (response.ok) {
        const tripsData = await response.json();
        // Sort by most recent first
        const sortedTrips = tripsData.sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at));
        
        // Separate current trips (not completed/cancelled) vs completed trips
        const currentTrips = sortedTrips.filter(trip => 
          !['completed', 'cancelled'].includes(trip.status)
        );
        const completedTrips = sortedTrips.filter(trip => 
          ['completed', 'cancelled'].includes(trip.status)
        );
        
        setTrips(currentTrips);
        setCompletedTrips(completedTrips);

        // Check for driver notifications (on_way, arrived, no_driver)
        const notif = currentTrips.find(t => 
          t.rider_notification && !t.rider_notification.seen
        );
        if (notif?.rider_notification) {
          setDriverNotification(notif.rider_notification);
        }
      } else {
        setError('Failed to load trips');
      }
    } catch (error) {
      setError('Network error loading trips');
      console.error('Failed to load trips:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTripCreated = (newTrip) => {
    setTrips(prev => [newTrip, ...prev]);
    setActiveTab('trips');
    setTripHistoryTab('current');
    setReBookData(null);
  };

  const handleCancelTrip = async (tripId) => {
    if (!window.confirm('Are you sure you want to cancel this booking?')) return;
    try {
      setCancellingTrip(tripId);
      const response = await fetch(`${BACKEND_URL}/api/bookings/${tripId}/rider-cancel?user_id=${user.id}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` }
      });
      if (response.ok) {
        loadTrips();
      } else {
        const err = await response.json();
        alert(err.detail || 'Failed to cancel booking');
      }
    } catch {
      alert('Network error. Please try again.');
    } finally {
      setCancellingTrip(null);
    }
  };

  const handleRebook = (trip) => {
    setReBookData(trip);
    setActiveTab('book');
  };

  const handlePayTrip = async (trip) => {
    try {
      setPayingTrip(trip.id);
      const amount = trip.quoteDetails?.totalCost || trip.quoteDetails?.totalFare || 0;
      const callbackUrl = `${window.location.origin}${window.location.pathname}`;
      const response = await fetch(`${BACKEND_URL}/api/payments/initialize`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify({
          booking_id: trip.id,
          amount: parseFloat(amount),
          email: user?.email || trip.passengerEmail,
          callback_url: callbackUrl
        })
      });
      const data = await response.json();
      if (response.ok && data.authorization_url) {
        window.location.href = data.authorization_url;
      } else {
        alert(data.detail || 'Failed to initiate payment. Please try again.');
        setPayingTrip(null);
      }
    } catch {
      alert('Network error. Please try again.');
      setPayingTrip(null);
    }
  };

  const getPaymentBadge = (trip) => {
    if (trip.status === 'cancelled') return null;
    const ps = trip.payment_status;
    if (ps === 'paid') return <span data-testid="payment-badge-paid" style={{ background: '#dcfce7', color: '#166534', padding: '2px 8px', borderRadius: '12px', fontSize: '0.75rem', fontWeight: '600' }}>Paid</span>;
    if (ps === 'pending') return <span data-testid="payment-badge-pending" style={{ background: '#fef9c3', color: '#854d0e', padding: '2px 8px', borderRadius: '12px', fontSize: '0.75rem', fontWeight: '600' }}>Payment Pending</span>;
    if (ps === 'failed') return <span data-testid="payment-badge-failed" style={{ background: '#fee2e2', color: '#991b1b', padding: '2px 8px', borderRadius: '12px', fontSize: '0.75rem', fontWeight: '600' }}>Payment Failed</span>;
    return <span data-testid="payment-badge-unpaid" style={{ background: '#fef3c7', color: '#92400e', padding: '2px 8px', borderRadius: '12px', fontSize: '0.75rem', fontWeight: '600' }}>Unpaid</span>;
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'pending_assignment': { label: 'Pending Assignment', class: 'status-pending' },
      'assigned': { label: 'Driver Assigned', class: 'status-assigned' },
      'confirmed': { label: 'Confirmed', class: 'status-confirmed' },
      'in_progress': { label: 'In Progress', class: 'status-progress' },
      'completed': { label: 'Completed', class: 'status-completed' },
      'cancelled': { label: 'Cancelled', class: 'status-cancelled' }
    };

    const config = statusConfig[status] || { label: status, class: 'status-unknown' };
    return <span className={`status-badge ${config.class}`}>{config.label}</span>;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-ZA', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleRateTrip = (trip) => {
    setRatingTrip(trip);
    setRating(0);
    setFeedback('');
  };

  const submitRating = async () => {
    if (rating === 0) {
      alert('Please select a rating');
      return;
    }

    try {
      setSubmittingRating(true);
      const response = await fetch(`${BACKEND_URL}/api/bookings/${ratingTrip.id}/rate`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify({
          rating,
          feedback
        })
      });

      if (response.ok) {
        // Update the trip in completed trips
        setCompletedTrips(prev => prev.map(trip => 
          trip.id === ratingTrip.id 
            ? { ...trip, rating, rating_feedback: feedback }
            : trip
        ));
        
        setRatingTrip(null);
        alert('Thank you for rating your trip!');
      } else {
        alert('Failed to submit rating. Please try again.');
      }
    } catch (error) {
      console.error('Error submitting rating:', error);
      alert('Failed to submit rating. Please try again.');
    } finally {
      setSubmittingRating(false);
    }
  };

  const renderStarRating = (currentRating, editable = false) => {
    const stars = [];
    for (let i = 1; i <= 5; i++) {
      stars.push(
        <button
          key={i}
          type="button"
          className={`star ${i <= currentRating ? 'filled' : ''}`}
          onClick={editable ? () => setRating(i) : undefined}
          style={{ cursor: editable ? 'pointer' : 'default' }}
        >
          ⭐
        </button>
      );
    }
    return <div className="star-rating">{stars}</div>;
  };

  return (
    <div className="rider-dashboard">
      <div className="dashboard-welcome">
        <div className="welcome-content">
          <h1>Welcome, {user?.name}</h1>
          <div className="dashboard-nav">
            <button 
              className={`nav-btn ${activeTab === 'book' ? 'active' : ''}`}
              onClick={() => setActiveTab('book')}
            >
              📝 Book Trip
            </button>
            <button 
              className={`nav-btn ${activeTab === 'trips' ? 'active' : ''}`}
              onClick={() => setActiveTab('trips')}
            >
              🚗 My Trips ({trips.length})
            </button>
          </div>
        </div>
      </div>

      <div className="dashboard-content">
        {/* Driver notification banner */}
        {driverNotification && (
          <div style={{
            background: '#A0306A', color: 'white', padding: '1rem 1.5rem',
            borderRadius: '12px', margin: '0 0 1rem 0', display: 'flex',
            justifyContent: 'space-between', alignItems: 'center',
            animation: 'fadeIn 0.3s ease'
          }} data-testid="driver-notification-banner">
            <span>🔔 {driverNotification.message}</span>
            <button onClick={() => setDriverNotification(null)} style={{ background: 'none', border: '1px solid rgba(255,255,255,0.5)', color: 'white', borderRadius: '6px', padding: '0.25rem 0.75rem', cursor: 'pointer' }}>Dismiss</button>
          </div>
        )}

        {activeTab === 'book' && (
          <TripBookingForm onTripCreated={handleTripCreated} reBookData={reBookData} />
        )}

        {activeTab === 'trips' && (
          <div className="trips-section">
            <div className="trips-header">
              <h2>My Trip History</h2>
              <div className="trip-tabs">
                <button 
                  className={tripHistoryTab === 'current' ? 'tab-active' : 'tab-inactive'}
                  onClick={() => setTripHistoryTab('current')}
                >
                  Current Trips ({trips.length})
                </button>
                <button 
                  className={tripHistoryTab === 'history' ? 'tab-active' : 'tab-inactive'}
                  onClick={() => setTripHistoryTab('history')}
                >
                  Trip History ({completedTrips.length})
                </button>
              </div>
            </div>

            {loading && (
              <div className="loading-message">
                <p>🔄 Loading trips...</p>
              </div>
            )}

            {error && (
              <div className="error-message">
                <p>❌ {error}</p>
              </div>
            )}

            {tripHistoryTab === 'current' && (
              <div className="current-trips-tab">
                {trips.length === 0 && !loading ? (
                  <div className="no-trips">
                    <h3>No current trips</h3>
                    <p>You don't have any active trips at the moment.</p>
                    <button 
                      onClick={() => setActiveTab('book')}
                      className="book-trip-btn"
                    >
                      Book Your Next Trip
                    </button>
                  </div>
                ) : (
                  <div className="trips-list">
                    {trips.map(trip => (
                      <div key={trip.id} className="trip-card">
                        <div className="trip-header">
                          <div className="trip-route">
                            <h4>📍 {trip.pickupAddress}</h4>
                            <div className="route-arrow">↓</div>
                            <h4>🏁 {trip.destinationAddress}</h4>
                          </div>
                          <div className="trip-status" style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'flex-end' }}>
                            {getStatusBadge(trip.status)}
                            {getPaymentBadge(trip)}
                          </div>
                        </div>

                        <div className="trip-details">
                          <div className="detail-row">
                            <span className="label">📅 Scheduled:</span>
                            <span className="value">{trip.scheduledDate} at {trip.scheduledTime}</span>
                          </div>
                          <div className="detail-row">
                            <span className="label">⏱️ Companion Time:</span>
                            <span className="value">{trip.companionMinutes} minutes</span>
                          </div>
                          {trip.quoteDetails && (
                            <>
                              <div className="detail-row">
                                <span className="label">📏 Distance:</span>
                                <span className="value">{trip.quoteDetails.distance || trip.quoteDetails.totalDistance}km</span>
                              </div>
                              <div className="detail-row">
                                <span className="label">💰 Fare:</span>
                                <span className="value fare">R{trip.quoteDetails.totalFare || trip.quoteDetails.totalCost}</span>
                              </div>
                            </>
                          )}
                        </div>

                        {trip.driverInfo && (
                          <div className="driver-info">
                            <h5>👨‍✈️ Assigned Driver</h5>
                            <div className="driver-details">
                              <div className="detail-row">
                                <span className="label">Name:</span>
                                <span className="value">{trip.driverInfo.name}</span>
                              </div>
                              <div className="detail-row">
                                <span className="label">Phone:</span>
                                <span className="value">{trip.driverInfo.phone}</span>
                              </div>
                              <div className="detail-row">
                                <span className="label">Vehicle:</span>
                                <span className="value">{trip.driverInfo.vehicle}</span>
                              </div>
                            </div>
                          </div>
                        )}

                        {trip.specialRequirements && (
                          <div className="special-requirements">
                            <h5>📋 Special Requirements</h5>
                            <div className="requirements">
                              {trip.wheelchairAccess && <span className="requirement">♿ Wheelchair Access</span>}
                              {trip.elderlyAssistance && <span className="requirement">👴 Elderly Assistance</span>}
                              {trip.medicalAppointment && <span className="requirement">🏥 Medical Appointment</span>}
                              {trip.shoppingAssistance && <span className="requirement">🛒 Shopping Assistance</span>}
                              {trip.specialRequirements && <span className="requirement">📝 {trip.specialRequirements}</span>}
                            </div>
                          </div>
                        )}

                        <div className="trip-footer">
                          <small className="trip-date">
                            Booked: {formatDate(trip.created_at)}
                          </small>
                          
                          {trip.status === 'assigned' && (
                            <div className="trip-actions">
                              <span className="assigned-message">⏳ Waiting for driver confirmation...</span>
                            </div>
                          )}
                          
                          {trip.status === 'confirmed' && (
                            <div className="trip-actions">
                              {trip.driver_status === 'on_way' && (
                                <span style={{ color: '#A0306A', fontWeight: '600' }}>🚗 Driver is on the way!</span>
                              )}
                              {trip.driver_status === 'arrived' && (
                                <span style={{ color: '#22c55e', fontWeight: '600' }}>📍 Driver has arrived!</span>
                              )}
                              {!trip.driver_status && (
                                <span className="confirmed-message">✅ Trip confirmed by driver! Ready to start.</span>
                              )}
                            </div>
                          )}
                          
                          {trip.status === 'in_progress' && (
                            <div className="trip-actions">
                              <span className="progress-message">🚗 Trip in progress...</span>
                            </div>
                          )}
                          
                          {trip.status === 'pending_assignment' && (
                            <div className="trip-actions">
                              <span className="pending-message">📋 Awaiting driver assignment...</span>
                              {trip.no_driver_available && (
                                <div style={{ background: '#fff3cd', border: '1px solid #ffc107', borderRadius: '8px', padding: '0.5rem', marginTop: '0.5rem', fontSize: '0.85rem', color: '#856404' }} data-testid="no-driver-message">
                                  ⚠️ {trip.no_driver_message || "No drivers available. Our team will contact you shortly."}
                                </div>
                              )}
                            </div>
                          )}

                          {/* Driver contact info */}
                          {trip.driverInfo && (trip.status === 'confirmed' || trip.status === 'in_progress') && (
                            <div className="driver-contact-actions" style={{ marginTop: '0.75rem', display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                              <a href={`tel:${trip.driverInfo.phone}`} style={{ padding: '0.4rem 0.8rem', background: '#A0306A', color: 'white', borderRadius: '20px', textDecoration: 'none', fontSize: '0.8rem' }} data-testid="call-driver-btn">
                                📞 Call Driver
                              </a>
                              <a href={`https://wa.me/${trip.driverInfo.phone?.replace(/\D/g, '')}`} target="_blank" rel="noreferrer" style={{ padding: '0.4rem 0.8rem', background: '#25D366', color: 'white', borderRadius: '20px', textDecoration: 'none', fontSize: '0.8rem' }} data-testid="whatsapp-driver-btn">
                                WhatsApp Driver
                              </a>
                            </div>
                          )}

                          {/* Cancel button */}
                          {['pending_assignment', 'assigned', 'confirmed'].includes(trip.status) && (
                            <div style={{ marginTop: '0.75rem' }}>
                              <button
                                onClick={() => handleCancelTrip(trip.id)}
                                disabled={cancellingTrip === trip.id}
                                data-testid="cancel-trip-btn"
                                style={{ padding: '0.4rem 1rem', background: 'white', border: '1px solid #dc2626', color: '#dc2626', borderRadius: '8px', fontSize: '0.8rem', cursor: 'pointer' }}
                              >
                                {cancellingTrip === trip.id ? 'Cancelling...' : 'Cancel Booking'}
                              </button>
                            </div>
                          )}

                          {/* Pay Now button for unpaid/failed trips */}
                          {['unpaid', 'failed', undefined, null].includes(trip.payment_status) && 
                           trip.status !== 'cancelled' && (
                            <div style={{ marginTop: '0.75rem' }}>
                              <button
                                onClick={() => handlePayTrip(trip)}
                                disabled={payingTrip === trip.id}
                                data-testid="pay-trip-btn"
                                style={{ padding: '0.5rem 1.25rem', background: '#A0306A', color: 'white', border: 'none', borderRadius: '8px', fontSize: '0.85rem', fontWeight: '600', cursor: 'pointer', width: '100%', opacity: payingTrip === trip.id ? 0.7 : 1 }}
                              >
                                {payingTrip === trip.id ? 'Redirecting to payment...' : 'Pay Now (Paystack)'}
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {tripHistoryTab === 'history' && (
              <div className="trip-history-tab">
                {completedTrips.length === 0 && !loading ? (
                  <div className="no-trips">
                    <h3>No completed trips yet</h3>
                    <p>Your completed trips will appear here once you finish some rides.</p>
                  </div>
                ) : (
                  <div className="trips-list">
                    {completedTrips.map(trip => (
                      <div key={trip.id} className="trip-card">
                        <div className="trip-header">
                          <div className="trip-route">
                            <h4>📍 {trip.pickupAddress}</h4>
                            <div className="route-arrow">↓</div>
                            <h4>🏁 {trip.destinationAddress}</h4>
                          </div>
                          <div className="trip-status" style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'flex-end' }}>
                            {getStatusBadge(trip.status)}
                            {getPaymentBadge(trip)}
                          </div>
                        </div>

                        <div className="trip-details">
                          <div className="detail-row">
                            <span className="label">📅 Completed:</span>
                            <span className="value">{formatDate(trip.updated_at)}</span>
                          </div>
                          <div className="detail-row">
                            <span className="label">⏱️ Companion Time:</span>
                            <span className="value">{trip.companionMinutes} minutes</span>
                          </div>
                          {trip.quoteDetails && (
                            <>
                              <div className="detail-row">
                                <span className="label">📏 Distance:</span>
                                <span className="value">{trip.quoteDetails.distance || trip.quoteDetails.totalDistance}km</span>
                              </div>
                              <div className="detail-row">
                                <span className="label">💰 Final Fare:</span>
                                <span className="value fare" data-testid="final-fare">
                                  R{trip.actual_fare ?? (trip.quoteDetails.totalFare || trip.quoteDetails.totalCost)}
                                  {trip.actual_fare && ((trip.pricing_adjustment && trip.pricing_adjustment !== 1) || trip.fare_adjusted_by_admin) && (
                                    <small style={{ color: '#888', fontSize: '0.75rem', marginLeft: '0.4rem', textDecoration: 'line-through' }}>
                                      est. R{trip.quoteDetails.totalFare || trip.quoteDetails.totalCost}
                                    </small>
                                  )}
                                </span>
                              </div>
                            </>
                          )}
                        </div>

                        {trip.driverInfo && (
                          <div className="driver-info">
                            <h5>👨‍✈️ Driver</h5>
                            <div className="driver-details">
                              <div className="detail-row">
                                <span className="label">Name:</span>
                                <span className="value">{trip.driverInfo.name}</span>
                              </div>
                              <div className="detail-row">
                                <span className="label">Vehicle:</span>
                                <span className="value">{trip.driverInfo.vehicle}</span>
                              </div>
                            </div>
                          </div>
                        )}

                        {trip.trip_notes && (
                          <div className="trip-notes">
                            <h5>📝 Trip Notes</h5>
                            <p>{trip.trip_notes}</p>
                          </div>
                        )}

                        <div className="trip-footer">
                          <small className="trip-date">
                            Originally Booked: {formatDate(trip.created_at)}
                          </small>
                          
                          {trip.status === 'completed' && (
                            <div className="rating-section">
                              {trip.rating ? (
                                <div className="existing-rating">
                                  <span className="rating-label">Your Rating:</span>
                                  {renderStarRating(trip.rating)}
                                  {trip.rating_feedback && (
                                    <p className="rating-feedback">"{trip.rating_feedback}"</p>
                                  )}
                                </div>
                              ) : (
                                <button 
                                  className="rate-trip-btn"
                                  onClick={() => handleRateTrip(trip)}
                                >
                                  ⭐ Rate This Trip
                                </button>
                              )}
                              <button
                                onClick={() => handleRebook(trip)}
                                data-testid="rebook-trip-btn"
                                style={{ marginTop: '0.5rem', padding: '0.4rem 1rem', background: 'white', border: '1px solid #A0306A', color: '#A0306A', borderRadius: '8px', fontSize: '0.8rem', cursor: 'pointer', display: 'block' }}
                              >
                                Rebook This Trip
                              </button>
                            </div>
                          )}
                          {trip.status === 'cancelled' && (
                            <div style={{ marginTop: '0.5rem' }}>
                              <small style={{ color: '#666' }}>{trip.cancelled_by === 'rider' ? 'Cancelled by you' : 'Booking cancelled'}</small>
                              <button
                                onClick={() => handleRebook(trip)}
                                data-testid="rebook-cancelled-trip-btn"
                                style={{ marginTop: '0.35rem', padding: '0.4rem 1rem', background: '#A0306A', color: 'white', border: 'none', borderRadius: '8px', fontSize: '0.8rem', cursor: 'pointer', display: 'block' }}
                              >
                                Rebook This Trip
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Rating Modal */}
      {ratingTrip && (
        <div className="modal-overlay" onClick={() => setRatingTrip(null)}>
          <div className="modal-content rating-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Rate Your Trip</h3>
              <button className="modal-close" onClick={() => setRatingTrip(null)}>×</button>
            </div>
            
            <div className="modal-body">
              <div className="trip-summary">
                <p><strong>Trip:</strong> {ratingTrip.pickupAddress} → {ratingTrip.destinationAddress}</p>
                <p><strong>Driver:</strong> {ratingTrip.driverInfo?.name}</p>
                <p><strong>Date:</strong> {formatDate(ratingTrip.completed_at || ratingTrip.updated_at)}</p>
              </div>
              
              <div className="rating-input">
                <label>How was your trip?</label>
                {renderStarRating(rating, true)}
              </div>
              
              <div className="feedback-input">
                <label htmlFor="trip-feedback">Additional feedback (optional):</label>
                <textarea
                  id="trip-feedback"
                  value={feedback}
                  onChange={(e) => setFeedback(e.target.value)}
                  placeholder="Tell us about your experience..."
                  rows="3"
                />
              </div>
              
              <div className="modal-actions">
                <button 
                  className="btn-secondary"
                  onClick={() => setRatingTrip(null)}
                  disabled={submittingRating}
                >
                  Cancel
                </button>
                <button 
                  className="btn-primary"
                  onClick={submitRating}
                  disabled={submittingRating || rating === 0}
                >
                  {submittingRating ? 'Submitting...' : 'Submit Rating'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RiderDashboard;